/**
 * Blinkit-inspired quick-commerce homepage — rendered ONLY for tenants whose theme preset declares
 * ``homeLayout: "blinkit"`` (lib/themes.js → HomePage.jsx). Every other tenant keeps its own home layout.
 *
 * Hierarchy (mobile reference, adapted for tablet/desktop):
 *   1. Blue header — brand + delivery ETA, delivery address, wallet / notifications / account
 *   2. Search (sticky, with rotating hints and the compact voice mic)
 *   3. Horizontal category tabs (business categories)              ┘ sticky together with search
 *   4. Hero banner carousel                                       ← admin banners (/feed)
 *   5. "Top collections" cards — two thumbnails + "+N more"       ← admin home sections
 *   6. "See all products" CTA                                     → /products catalog
 *   7. Group-category chips                                       ← existing GroupCategoryChipRow
 *   8. Featured stores — portrait cards                           ← featured shops (/feed)
 *   9. Offers                                                     ← coupons (/feed)
 *  10. Product rails, one per home section (shared BkProductTile)   ← /marketplace/home-sections
 *  11. In-store offers promo banner                               → /store-coupons
 * The bottom navigation and sticky cart come from the shared customer shell.
 *
 * Data: only existing endpoints, fetched once each (the header reuses MarketplaceHome's hooks). Prices are
 * displayed exactly as the server returns them — the home-sections endpoint already runs the pricing
 * engine (CLAUDE.md landmine 15); nothing is recomputed here.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Bell, ChevronDown, ChevronRight, LayoutGrid, Percent, Search, Ticket, User, Wallet,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatDeliveryEta } from "../lib/deliveryEta";
import { poweredByBrand } from "../lib/utils";
import { useCartQuantities } from "../lib/useCartQuantities";
import { useDefaultAddress, useUnreadNotifCount } from "../lib/storefrontHooks";
import AddressPickerModal from "./AddressPickerModal";
import BannerCarousel from "./BannerCarousel";
import BizCategoryIcon from "./BizCategoryIcon";
import GroupCategoryChipRow from "./GroupCategoryChipRow";
import RoleSwitcher from "./RoleSwitcher";
import BkProductTile from "./blinkit/BkProductTile";
import VoiceSearch from "./VoiceSearch";
import { CardSkeleton } from "./Loaders";
import LseHome from "./lse/LseHome";

const inr = (n) => `₹${Math.round(Number(n || 0))}`;

const firstImage = (p) => (p?.images || [])[0] || null;

/* ───────────────────────────── Header ───────────────────────────── */

function useWalletBalance() {
    const { user } = useAuth();
    const [balance, setBalance] = useState(null);
    useEffect(() => {
        if (!user) { setBalance(null); return undefined; }
        let alive = true;
        api.get("/wallet")
            .then(({ data }) => { if (alive) setBalance(Number(data?.wallet?.balance || 0)); })
            .catch(() => {});
        return () => { alive = false; };
    }, [user]);
    return balance;
}

/** Cycles through real catalogue words ("milk", category names…) for the search hint. */
function useRotatingHint(terms) {
    const [i, setI] = useState(0);
    useEffect(() => {
        if (terms.length < 2) return undefined;
        const id = setInterval(() => setI((x) => (x + 1) % terms.length), 3000);
        return () => clearInterval(id);
    }, [terms.length]);
    return terms.length ? terms[i % terms.length] : null;
}

function HeaderIconLink({ to, label, testid, children, dot }) {
    return (
        <Link to={to} aria-label={label} data-testid={testid}
              className="relative w-10 h-10 shrink-0 rounded-full flex items-center justify-center hover:bg-white/10 active:scale-95 transition">
            {children}
            {dot && <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500 ring-2 ring-primary" data-testid={`${testid}-dot`} />}
        </Link>
    );
}

function BlinkitHeader({ tenant, verticals, hintTerms }) {
    const { user } = useAuth();
    const addr = useDefaultAddress();
    const unread = useUnreadNotifCount();
    const wallet = useWalletBalance();
    const [picking, setPicking] = useState(false);
    const hint = useRotatingHint(hintTerms);

    const brand = tenant?.branding?.app_name || tenant?.name || "";
    const eta = formatDeliveryEta(tenant, null);        // only a tenant-configured ETA is shown, never invented
    const addrText = addr ? [addr.line1, addr.line2, addr.city].filter(Boolean).join(", ") : null;

    return (
        <>
            {/* Brand / ETA / address — scrolls away */}
            <div className="bg-primary text-primary-foreground px-4 pt-3 pb-2" data-testid="mp-header-blinkit">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <p className="text-[12px] font-bold leading-tight truncate" data-testid="bk-eyebrow">
                            {eta ? `${brand} in` : "Welcome to"}
                        </p>
                        <p className="text-[26px] sm:text-[28px] font-extrabold leading-[1.1] tracking-tight truncate" data-testid="bk-headline">
                            {eta || brand}
                        </p>
                        <button type="button" onClick={() => setPicking(true)} data-testid="bk-address"
                                className="mt-1 max-w-full inline-flex items-center gap-1 text-[13px] text-left hover:opacity-90">
                            <span className="truncate">
                                {addr ? (
                                    <><span className="font-extrabold uppercase">{addr.label || "Home"}</span> - {addrText}</>
                                ) : (
                                    <span className="font-semibold">Select delivery address</span>
                                )}
                            </span>
                            <ChevronDown size={16} className="shrink-0" />
                        </button>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 pt-1">
                        <RoleSwitcher />
                        {user && wallet !== null && (
                            <Link to="/wallet" data-testid="bk-wallet"
                                  className="h-9 inline-flex items-center gap-1 rounded-full bg-white text-foreground px-2.5 text-[12px] font-bold shadow-sm active:scale-95 transition">
                                <Wallet size={14} className="text-primary" /> {inr(wallet)}
                            </Link>
                        )}
                        <HeaderIconLink to="/notifications" label="Notifications" testid="bk-bell" dot={unread > 0}>
                            <Bell size={20} />
                        </HeaderIconLink>
                        <Link to="/profile" aria-label="Account" data-testid="bk-account"
                              className="w-10 h-10 shrink-0 rounded-full bg-foreground text-background flex items-center justify-center shadow-sm active:scale-95 transition">
                            <User size={18} />
                        </Link>
                    </div>
                </div>
            </div>

            {/* Search + category tabs — stay pinned while the page scrolls */}
            <div className="sticky top-0 z-40 bg-primary text-primary-foreground shadow-[0_6px_16px_-10px_rgba(15,23,42,0.45)]" data-testid="bk-sticky">
                <div className="px-4 pt-1.5 pb-3">
                    <div className="flex items-center bg-card text-foreground rounded-xl shadow-sm h-12 lg:max-w-3xl">
                        <Link to="/search" className="flex-1 min-w-0 flex items-center gap-2.5 h-full pl-3.5 pr-2" data-testid="bk-search">
                            <Search size={19} className="shrink-0" />
                            <span className="text-[14px] text-muted-foreground truncate">
                                {hint ? <>Search &ldquo;<span key={hint} className="fade-up inline-block">{hint}</span>&rdquo;</> : "Search products"}
                            </span>
                        </Link>
                        {tenant?.features?.voice_search && (
                            <div className="flex items-center h-full pr-1.5">
                                <span className="w-px h-6 bg-border mr-1" aria-hidden="true" />
                                <VoiceSearch compact />
                            </div>
                        )}
                    </div>
                </div>
                <CategoryTabs verticals={verticals} />
            </div>

            {picking && (
                <AddressPickerModal
                    initial={addr || null}
                    onClose={() => setPicking(false)}
                    onSaved={() => { setPicking(false); window.dispatchEvent(new Event("addresses:update")); }}
                />
            )}
        </>
    );
}

function CategoryTabs({ verticals }) {
    const list = (verticals || []).slice(0, 16);
    return (
        <nav className="flex gap-1 overflow-x-auto no-scrollbar px-2 lg:gap-3" aria-label="Categories" data-testid="mp-verticals-blinkit">
            <span className="relative flex flex-col items-center shrink-0 min-w-[64px] px-2 pt-0.5 pb-2.5" aria-current="page" data-testid="bk-tab-all">
                <LayoutGrid size={22} strokeWidth={2.2} />
                <span className="mt-1 text-[12px] font-bold">All</span>
                <span className="absolute bottom-0 left-3 right-3 h-[3px] rounded-t-full bg-white" />
            </span>
            {list.map((v) => (
                <Link key={v.id || v.slug} to={`/c/${v.slug}/products`} data-testid={`mp-vertical-${v.slug}`}
                      className="flex flex-col items-center shrink-0 min-w-[64px] max-w-[92px] px-2 pt-0.5 pb-2.5 opacity-85 hover:opacity-100 transition">
                    {v.icon_url ? (
                        <span className="w-[22px] h-[22px] rounded-md bg-white/95 flex items-center justify-center overflow-hidden">
                            <img src={v.icon_url} alt="" className="w-[18px] h-[18px] object-contain" />
                        </span>
                    ) : (
                        <BizCategoryIcon name={v.icon} size={22} />
                    )}
                    <span className="mt-1 text-[12px] font-medium truncate max-w-full">{v.name || v.label || v.slug}</span>
                </Link>
            ))}
        </nav>
    );
}

/* ─────────────────────────── Sections ─────────────────────────── */

function SectionHeading({ title, action, id }) {
    return (
        <div className="flex items-end justify-between gap-3 mb-3" id={id}>
            <h2 className="text-[18px] sm:text-[19px] font-extrabold tracking-tight leading-tight">{title}</h2>
            {action}
        </div>
    );
}

function TopCollections({ sections }) {
    const list = sections.slice(0, 6);
    if (!list.length) return null;
    return (
        <section className="px-4 mt-6" data-testid="bk-collections">
            <SectionHeading title="Top collections" />
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                {list.map((s) => {
                    const imgs = s.products.map(firstImage).filter(Boolean);
                    const more = Math.max(0, s.products.length - 2);
                    return (
                        <a key={s.id} href={`#bk-sec-${s.slug}`} data-testid={`bk-collection-${s.slug}`}
                           className="group flex flex-col items-stretch min-w-0 active:scale-[0.98] transition">
                            <div className="relative rounded-2xl bg-primary/[0.07] p-1.5 pb-3.5">
                                <div className="grid grid-cols-2 gap-1">
                                    {[0, 1].map((k) => (
                                        <div key={k} className="aspect-square rounded-xl bg-card overflow-hidden flex items-center justify-center">
                                            {imgs[k] ? (
                                                <img src={imgs[k]} alt="" loading="lazy" className="w-full h-full object-contain p-1 group-hover:scale-105 transition duration-300" />
                                            ) : (
                                                <LayoutGrid size={18} className="text-primary/30" />
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {more > 0 && (
                                    <span className="absolute left-1/2 -translate-x-1/2 -bottom-2.5 rounded-full bg-card border border-border text-[10px] font-semibold text-muted-foreground px-2 py-0.5 whitespace-nowrap shadow-sm">
                                        +{more} more
                                    </span>
                                )}
                            </div>
                            <p className="mt-3.5 text-[13px] font-semibold text-center leading-snug line-clamp-2">{s.title}</p>
                        </a>
                    );
                })}
            </div>
        </section>
    );
}

function SeeAllProducts({ sections }) {
    const thumbs = sections.flatMap((s) => s.products.map(firstImage)).filter(Boolean).slice(0, 3);
    return (
        <section className="px-4 mt-5">
            <Link to="/products" data-testid="bk-see-all"
                  className="flex items-center justify-center gap-3 rounded-2xl bg-primary/[0.07] border border-primary/15 py-3 px-4 active:scale-[0.99] transition hover:bg-primary/10">
                {thumbs.length > 0 && (
                    <span className="flex -space-x-2.5" aria-hidden="true">
                        {thumbs.map((src, k) => (
                            <span key={k} className="w-8 h-8 rounded-full bg-card border-2 border-card shadow-sm overflow-hidden">
                                <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </span>
                        ))}
                    </span>
                )}
                <span className="text-[15px] font-bold text-primary">See all products</span>
                <ChevronRight size={18} className="text-primary" />
            </Link>
        </section>
    );
}

function FeaturedStores({ shops }) {
    const list = (shops || []).slice(0, 12);
    if (!list.length) return null;
    return (
        <section className="mt-7" data-testid="bk-featured">
            <div className="px-4"><SectionHeading title="Featured stores" /></div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar px-4 scroll-px-4 pb-1 snap-x snap-mandatory">
                {list.map((r) => {
                    const img = r.cover_image_url || r.logo_url;
                    return (
                        <Link key={r.id} to={`/restaurant/${r.id}`} data-testid={`bk-featured-${r.id}`}
                              className="snap-start shrink-0 w-[42%] sm:w-[31%] md:w-[23%] lg:w-[18%] rounded-2xl p-[3px] bg-gradient-to-b from-primary to-primary/60 shadow-sm active:scale-[0.98] transition">
                            <div className="relative aspect-[3/4] rounded-[13px] overflow-hidden bg-muted">
                                {img ? (
                                    <img src={img} alt={r.name} loading="lazy" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-4xl font-extrabold text-primary/30">
                                        {(r.name || "?").slice(0, 1)}
                                    </div>
                                )}
                                <span className="absolute top-0 left-1/2 -translate-x-1/2 rounded-b-lg bg-card text-primary text-[10px] font-bold px-2.5 py-0.5">
                                    Featured
                                </span>
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-2.5 pt-8 pb-2.5">
                                    <p className="text-white text-[13px] font-bold leading-tight line-clamp-2">{r.name}</p>
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </section>
    );
}

function Offers({ coupons }) {
    const list = (coupons || []).slice(0, 8);
    if (!list.length) return null;
    return (
        <section className="mt-7" data-testid="mp-coupon-strip">
            <div className="px-4">
                <SectionHeading title="Offers for you"
                    action={<Link to="/store-coupons" className="text-[13px] font-bold text-primary inline-flex items-center shrink-0">See all <ChevronRight size={14} /></Link>} />
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar px-4 pb-1">
                {list.map((c) => (
                    <Link key={c.id} to="/store-coupons" data-testid={`mp-coupon-${c.code}`}
                          className="shrink-0 w-[70%] sm:w-[46%] md:w-[31%] lg:w-[24%] rounded-2xl bg-foreground text-background p-4 active:scale-[0.99] transition">
                        <span className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground text-[11px] font-bold px-2 py-0.5">
                            <Percent size={12} /> {c.code}
                        </span>
                        <p className="mt-2 text-[15px] font-bold leading-snug line-clamp-1">{c.title}</p>
                        {c.description && <p className="text-[12px] opacity-70 leading-snug line-clamp-2 mt-0.5">{c.description}</p>}
                    </Link>
                ))}
            </div>
        </section>
    );
}

function ProductRail({ section, cartMap }) {
    if (!section.products?.length) return null;
    return (
        // scroll-mt clears the sticky search + tabs when a "Top collections" card jumps here.
        <section className="mt-7 scroll-mt-[150px]" id={`bk-sec-${section.slug}`} data-testid={`mp-section-${section.slug}`}>
            <div className="px-4">
                <SectionHeading
                    title={section.title}
                    action={<Link to="/products" className="text-[13px] font-bold text-primary inline-flex items-center shrink-0">See all <ChevronRight size={14} /></Link>}
                />
                {section.subtitle && <p className="-mt-2 mb-3 text-[12px] text-muted-foreground line-clamp-1">{section.subtitle}</p>}
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar px-4 pb-1" data-testid={`mp-section-grid-${section.slug}`}>
                {section.products.map((p) => <BkProductTile key={p.id} product={p} qty={cartMap[p.id] || 0} layout="rail" testidPrefix="mp-product" />)}
            </div>
        </section>
    );
}

function StoreOffersBanner() {
    return (
        <section className="px-4 mt-8">
            <Link to="/store-coupons" data-testid="bk-promo-store-offers"
                  className="relative block overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-foreground text-primary-foreground p-5 sm:p-6 active:scale-[0.99] transition">
                <Ticket size={120} strokeWidth={1} className="absolute -right-6 -bottom-8 opacity-15" aria-hidden="true" />
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-80">In-store offers</p>
                <p className="mt-1 text-[20px] sm:text-[24px] font-extrabold leading-tight max-w-[18ch]">Unlock coupons with your coins</p>
                <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-white text-foreground text-[12px] font-bold px-3 py-1.5">
                    Explore offers <ChevronRight size={14} />
                </span>
            </Link>
        </section>
    );
}

function EmptyCatalog() {
    return (
        <div className="mx-4 mt-6 rounded-2xl border border-dashed border-border bg-card p-6 text-center" data-testid="mp-empty-sections">
            <LayoutGrid size={28} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm font-bold">Products are on their way</p>
            <p className="text-xs text-muted-foreground mt-1">Browse everything available right now in the catalogue.</p>
            <Link to="/products" className="mt-3 inline-flex rounded-xl bg-primary text-primary-foreground px-4 py-2 text-xs font-bold">
                Browse all products
            </Link>
        </div>
    );
}

/* ─────────────────────────── Page ─────────────────────────── */

export default function BlinkitHome({ feed, verticals, tenant }) {
    const [sections, setSections] = useState(null);
    const cartMap = useCartQuantities();

    useEffect(() => {
        let alive = true;
        api.get("/marketplace/home-sections", { params: { variant: "blinkit" } })
            .then(({ data }) => { if (alive) setSections(Array.isArray(data) ? data.filter((s) => s.products?.length) : []); })
            .catch(() => { if (alive) setSections([]); });
        return () => { alive = false; };
    }, []);

    // Real words from this tenant's catalogue for the rotating search hint.
    const hintTerms = useMemo(() => {
        const words = [
            ...(sections || []).flatMap((s) => s.products.slice(0, 2).map((p) => p.name)),
            ...(verticals || []).map((v) => v.name),
        ].filter((w) => w && w.length <= 28);
        return [...new Set(words)].slice(0, 8);
    }, [sections, verticals]);

    const featured = feed?.featured_restaurants?.length ? feed.featured_restaurants : [];

    return (
        <div className="pb-32 min-w-0" data-testid="marketplace-home" data-variant="blinkit">
            <BlinkitHeader tenant={tenant} verticals={verticals} hintTerms={hintTerms} />

            {/* One banner at a time on phones; the next one peeks on tablets; two side by side on desktop. */}
            <div className="mt-3 [--bk-banner-w:92%] md:[--bk-banner-w:64%] lg:[--bk-banner-w:48.5%]" data-testid="bk-hero">
                <BannerCarousel banners={feed?.banners || []} slideWidth="var(--bk-banner-w)" />
            </div>

            {/* Location Service Engine tenants only (renders nothing otherwise). */}
            <LseHome />

            {sections === null ? (
                <div className="px-4 mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="mp-loading">
                    <CardSkeleton /><CardSkeleton /><CardSkeleton />
                </div>
            ) : sections.length === 0 ? (
                <EmptyCatalog />
            ) : (
                <>
                    <TopCollections sections={sections} />
                    <SeeAllProducts sections={sections} />
                </>
            )}

            <div className="mt-5">
                <GroupCategoryChipRow testIdPrefix="mp-group-chips-blinkit" />
            </div>

            <FeaturedStores shops={featured} />
            <Offers coupons={feed?.coupons} />

            {(sections || []).map((s) => <ProductRail key={s.id} section={s} cartMap={cartMap} />)}

            <StoreOffersBanner />

            <footer className="text-center pt-10 pb-6" data-testid="mp-footer">
                <p className="text-sm font-semibold text-foreground/80">{tenant?.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                    Powered by {poweredByBrand(tenant?.branding?.powered_by_text)}
                </p>
            </footer>
        </div>
    );
}
